import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ArrowUturnLeftIcon,
  EyeIcon,
  KeyIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  DetailItem,
  Field,
  FormActions,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import UserForm, { generatePassword, isPasswordValid, PasswordChecklist, PasswordInput } from '../../components/users/UserForm';
import {
  useCreateUser,
  useDeleteUser,
  useResetTwoFactor,
  useResetUserPassword,
  useRestoreUser,
  useRoles,
  useUpdateUser,
  useUserActivities,
  useUsersPage,
  useUserSummary,
} from '../../hooks/useUsers';
import { CreateUserDto, UpdateUserDto, User } from '../../services/user.service';
import { useAuthStore } from '../../store/auth.store';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const SORTS = [
  { value: 'newest', label: 'Newest first', sortBy: 'createdAt', sortOrder: 'DESC' },
  { value: 'name', label: 'Name A–Z', sortBy: 'firstName', sortOrder: 'ASC' },
  { value: 'email', label: 'Email A–Z', sortBy: 'email', sortOrder: 'ASC' },
  { value: 'login', label: 'Last sign-in', sortBy: 'lastLoginAt', sortOrder: 'DESC' },
] as const;

const STATUS_TONES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 ring-green-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  suspended: 'bg-red-50 text-red-700 ring-red-600/20',
  inactive: 'bg-gray-50 text-gray-600 ring-gray-500/20',
  deleted: 'bg-gray-100 text-gray-600 ring-gray-500/20',
};

const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

const fullName = (user: User) => [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ') || user.email;
const initials = (user: User) =>
  ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')).toUpperCase() || user.email[0].toUpperCase();
const isLocked = (user: User) => !!user.lockedUntil && new Date(user.lockedUntil) > new Date();

const Avatar: React.FC<{ user: User; size?: 'sm' | 'lg' }> = ({ user, size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'h-12 w-12 text-base' : 'h-9 w-9 text-sm';
  return user.profilePicture ? (
    <img src={user.profilePicture} alt="" className={cn('shrink-0 rounded-full object-cover', sizeClass)} />
  ) : (
    <span className={cn('flex shrink-0 items-center justify-center rounded-full bg-indigo-100 font-semibold text-indigo-700', sizeClass)}>
      {initials(user)}
    </span>
  );
};

const StatusBadges: React.FC<{ user: User }> = ({ user }) => (
  <div className="flex flex-wrap items-center gap-1">
    <span className={cn(badgeClass, STATUS_TONES[user.deletedAt ? 'deleted' : user.status])}>
      {user.deletedAt ? 'Deleted' : titleCase(user.status)}
    </span>
    {!user.deletedAt && isLocked(user) && (
      <span className={cn(badgeClass, 'inline-flex items-center gap-1 bg-red-50 text-red-700 ring-red-600/20')}>
        <LockClosedIcon className="h-3 w-3" />
        Locked
      </span>
    )}
    {!user.deletedAt && !user.emailVerified && (
      <span className={cn(badgeClass, 'bg-amber-50 text-amber-700 ring-amber-600/20')}>Email not verified</span>
    )}
  </div>
);

const UserList: React.FC = () => {
  const currentUser = useAuthStore((state) => state.user) as any;
  const currentUserId: string | undefined = currentUser?.id;
  const currentUserRole: string | undefined = typeof currentUser?.role === 'string' ? currentUser.role : currentUser?.role?.name;
  const { can, permissions: currentUserPermissions } = usePermissions();
  // Editing, resetting passwords and restoring need "users.update"; deleting needs "users.delete"
  const isAdmin = can('users.update');
  const canDelete = can('users.delete');
  const canAdd = can('users.create');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // '' is every user that is not deleted; 'deleted' is the deleted ones
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [sort, setSort] = useState<(typeof SORTS)[number]['value']>('newest');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [viewing, setViewing] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [turningOffTwoFactor, setTurningOffTwoFactor] = useState<User | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const activeSort = SORTS.find((option) => option.value === sort) ?? SORTS[0];
  const { data, isLoading, isError, error, isFetching, refetch } = useUsersPage({
    page,
    limit,
    search: search || undefined,
    status: statusFilter && statusFilter !== 'deleted' ? statusFilter : undefined,
    onlyDeleted: statusFilter === 'deleted' ? true : undefined,
    roleId: roleFilter || undefined,
    department: departmentFilter || undefined,
    sortBy: activeSort.sortBy,
    sortOrder: activeSort.sortOrder,
  });
  const { data: summary } = useUserSummary();
  const { data: roles = [] } = useRoles();
  const { data: activities = [], isLoading: activitiesLoading } = useUserActivities(viewing?.id ?? null, 8);

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const restoreUser = useRestoreUser();
  const resetPassword = useResetUserPassword();
  const resetTwoFactor = useResetTwoFactor();

  const confirmTwoFactorReset = () => {
    if (!turningOffTwoFactor) return;
    const user = turningOffTwoFactor;
    resetTwoFactor.mutate(user.id, {
      onSuccess: () => {
        toast.success(`Two-step sign-in turned off for ${fullName(user)}`);
        setTurningOffTwoFactor(null);
        setViewing((current) => (current && current.id === user.id ? { ...current, twoFactorEnabled: false } : current));
      },
      onError: (resetError) => toast.error(getApiErrorMessage(resetError, 'Two-step sign-in could not be turned off')),
    });
  };

  const users = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Step back when the last page no longer exists
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter || !!roleFilter || !!departmentFilter;
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
    setDepartmentFilter('');
    setPage(1);
  };
  const showStatus = (status: string) => {
    setStatusFilter((current) => (current === status ? '' : status));
    setPage(1);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (user: User) => {
    setViewing(null);
    setEditing(user);
    setFormOpen(true);
  };
  const isSaving = createUser.isPending || updateUser.isPending;
  const closeForm = () => {
    if (!isSaving) setFormOpen(false);
  };

  const handleCreate = (input: CreateUserDto) => {
    createUser.mutate(input, {
      onSuccess: (saved) => {
        toast.success(`${fullName(saved)} added${input.sendWelcomeEmail ? '. A welcome email is on its way.' : ''}`);
        setFormOpen(false);
      },
      onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The user could not be added')),
    });
  };

  const handleUpdate = (input: UpdateUserDto) => {
    if (!editing) return;
    updateUser.mutate(
      { id: editing.id, data: input },
      {
        onSuccess: (saved) => {
          toast.success(`${fullName(saved)} updated`);
          setFormOpen(false);
        },
        onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The user could not be updated')),
      },
    );
  };

  const handleDelete = () => {
    if (!deleting) return;
    deleteUser.mutate(deleting.id, {
      onSuccess: () => {
        toast.success(`${fullName(deleting)} deleted`);
        setDeleting(null);
      },
      onError: (actionError) => {
        toast.error(getApiErrorMessage(actionError, 'The user could not be deleted'));
        setDeleting(null);
      },
    });
  };

  const handleRestore = (user: User) => {
    restoreUser.mutate(user.id, {
      onSuccess: () => toast.success(`${fullName(user)} restored`),
      onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The user could not be restored')),
    });
  };

  const closeReset = () => {
    if (resetPassword.isPending) return;
    setResetting(null);
    setNewPassword('');
  };
  const handleResetPassword = (event: React.FormEvent) => {
    event.preventDefault();
    if (!resetting || !isPasswordValid(newPassword)) return;
    resetPassword.mutate(
      { id: resetting.id, newPassword },
      {
        onSuccess: () => {
          toast.success(`Password reset for ${fullName(resetting)}`);
          setResetting(null);
          setNewPassword('');
        },
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The password could not be reset')),
      },
    );
  };

  const stats = [
    { key: '', label: 'Users', value: summary?.total, hint: 'Not counting deleted' },
    { key: 'active', label: 'Active', value: summary?.active, hint: 'Can sign in' },
    { key: 'pending', label: 'Pending', value: summary?.pending, hint: 'Email not verified yet' },
    {
      key: 'suspended',
      label: 'Suspended',
      value: summary?.suspended,
      hint: summary?.locked ? `${summary.locked} locked by failed sign-ins` : 'Blocked from signing in',
    },
    { key: 'deleted', label: 'Deleted', value: summary?.deleted, hint: 'Can be restored' },
  ];

  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const iconButton = 'rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">The people who can sign in, their roles and the state of their accounts.</p>
        </div>
        <button type="button" onClick={openAdd} disabled={!canAdd} title={canAdd ? undefined : 'Your role does not allow adding users'} className={primaryButtonClass}>
          <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
          Add User
        </button>
      </div>

      {/* Clicking a card filters the list by that status */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {stats.map((stat) => {
          const selected = statusFilter === stat.key && stat.key !== '';
          return (
            <button
              key={stat.label}
              type="button"
              onClick={() => (stat.key === '' ? showStatus(statusFilter) : showStatus(stat.key))}
              aria-pressed={selected}
              className={cn(
                'rounded-lg bg-white p-4 text-left shadow-sm ring-1 transition hover:ring-indigo-300',
                selected ? 'ring-2 ring-indigo-500' : 'ring-gray-200',
              )}
            >
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value === undefined ? '—' : formatNumber(stat.value)}</p>
              <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
            </button>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 xl:flex-row xl:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, email, username or employee ID"
              aria-label="Search users"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex">
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              aria-label="Status"
              className={cn(inputClass, 'sm:w-40')}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
              <option value="deleted">Deleted</option>
            </select>
            <select
              value={roleFilter}
              onChange={(event) => {
                setRoleFilter(event.target.value);
                setPage(1);
              }}
              aria-label="Role"
              className={cn(inputClass, 'sm:w-40')}
            >
              <option value="">All roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.displayName}
                </option>
              ))}
            </select>
            <select
              value={departmentFilter}
              onChange={(event) => {
                setDepartmentFilter(event.target.value);
                setPage(1);
              }}
              aria-label="Department"
              disabled={!summary?.departments.length && !departmentFilter}
              className={cn(inputClass, 'sm:w-44')}
            >
              <option value="">All departments</option>
              {(summary?.departments ?? []).map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(event) => {
                setSort(event.target.value as typeof sort);
                setPage(1);
              }}
              aria-label="Sort"
              className={cn(inputClass, 'sm:w-40')}
            >
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The users could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>User</th>
                  <th scope="col" className={thClass}>Role</th>
                  <th scope="col" className={thClass}>Department</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={thClass}>Last sign-in</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 6 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">
                        {statusFilter === 'deleted' && !searchInput ? 'No deleted users' : hasFilters ? 'No user matches' : 'No users yet'}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasFilters ? 'Try a different search or filter.' : 'Add the people who need to sign in.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => {
                    const isSelf = user.id === currentUserId;
                    return (
                      <tr key={user.id} className={cn('hover:bg-gray-50', user.deletedAt && 'bg-gray-50/60')}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar user={user} />
                            <div className="min-w-0">
                              <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                <span className="truncate">{fullName(user)}</span>
                                {isSelf && <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>You</span>}
                              </p>
                              <p className="truncate text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className={cn(badgeClass, 'bg-gray-50 text-gray-700 ring-gray-500/20')}>
                            {user.role?.displayName || user.role?.name || 'No role'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900">{user.department || <span className="text-gray-400">—</span>}</p>
                          {user.designation && <p className="text-sm text-gray-500">{user.designation}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadges user={user} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatDateTime(user.lastLoginAt) ?? <span className="text-gray-400">Never</span>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={() => setViewing(user)} title="View" aria-label={`View ${fullName(user)}`} className={iconButton}>
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            {isAdmin && user.deletedAt && (
                              <button
                                type="button"
                                onClick={() => handleRestore(user)}
                                disabled={restoreUser.isPending}
                                className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                              >
                                <ArrowUturnLeftIcon className="mr-1 h-3.5 w-3.5" />
                                Restore
                              </button>
                            )}
                            {isAdmin && !user.deletedAt && (
                              <>
                                <button type="button" onClick={() => openEdit(user)} title="Edit" aria-label={`Edit ${fullName(user)}`} className={iconButton}>
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewPassword('');
                                    setResetting(user);
                                  }}
                                  title="Reset password"
                                  aria-label={`Reset password of ${fullName(user)}`}
                                  className={iconButton}
                                >
                                  <KeyIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleting(user)}
                                  disabled={isSelf || !canDelete}
                                  title={isSelf ? 'You cannot delete your own account' : canDelete ? 'Delete' : 'Your role does not allow deleting users'}
                                  aria-label={`Delete ${fullName(user)}`}
                                  className={cn(iconButton, !isSelf && 'hover:bg-red-50 hover:text-red-600')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      {/* Add / edit */}
      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? `Edit ${fullName(editing)}` : 'Add User'}
        description={editing ? 'Changes apply the next time the user signs in.' : 'Create an account and choose what the user may do.'}
        size="xl"
      >
        {formOpen && (
          <UserForm
            key={editing?.id ?? 'new'}
            user={editing}
            roles={roles}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            currentUserPermissions={currentUserPermissions}
            isSaving={isSaving}
            onCancel={closeForm}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
          />
        )}
      </Modal>

      {/* View */}
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title="User details" size="lg">
        {viewing && (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <Avatar user={viewing} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-semibold text-gray-900">{fullName(viewing)}</p>
                <p className="truncate text-sm text-gray-500">{viewing.email}</p>
                <div className="mt-1.5">
                  <StatusBadges user={viewing} />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200">
              <p className="border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Account</p>
              <dl className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3">
                <DetailItem label="Username">{viewing.username || '—'}</DetailItem>
                <DetailItem label="Role">{viewing.role?.displayName || viewing.role?.name || '—'}</DetailItem>
                <DetailItem label="User type">{titleCase(viewing.userType)}</DetailItem>
                <DetailItem label="Last sign-in">{formatDateTime(viewing.lastLoginAt) ?? 'Never'}</DetailItem>
                <DetailItem label="Added">{formatDateTime(viewing.createdAt)}</DetailItem>
                <DetailItem label="Two-step sign-in">
                  {viewing.twoFactorEnabled ? 'On' : 'Off'}
                  {viewing.twoFactorEnabled && isAdmin && !viewing.deletedAt && (
                    <button type="button" onClick={() => setTurningOffTwoFactor(viewing)} className="ml-2 text-xs font-medium text-red-600 hover:text-red-500">
                      Turn off
                    </button>
                  )}
                </DetailItem>
                {isLocked(viewing) && <DetailItem label="Locked until">{formatDateTime(viewing.lockedUntil)}</DetailItem>}
                {viewing.deletedAt && <DetailItem label="Deleted">{formatDateTime(viewing.deletedAt)}</DetailItem>}
              </dl>
            </div>

            <div className="rounded-lg border border-gray-200">
              <p className="border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Work and contact</p>
              <dl className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3">
                <DetailItem label="Employee ID">{viewing.employeeId || '—'}</DetailItem>
                <DetailItem label="Department">{viewing.department || '—'}</DetailItem>
                <DetailItem label="Designation">{viewing.designation || '—'}</DetailItem>
                <DetailItem label="Phone">{viewing.phone || '—'}</DetailItem>
                <DetailItem label="Mobile">{viewing.mobile || '—'}</DetailItem>
              </dl>
            </div>

            <div className="rounded-lg border border-gray-200">
              <p className="border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Recent activity</p>
              {activitiesLoading ? (
                <div className="space-y-2 p-4">
                  <div className="h-4 animate-pulse rounded bg-gray-100" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
              ) : activities.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">Nothing recorded for this user yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {activities.map((activity) => (
                    <li key={activity.id} className="flex items-start justify-between gap-4 px-4 py-2.5">
                      <p className="text-sm text-gray-700">{activity.description || titleCase(activity.activityType)}</p>
                      <p className="shrink-0 text-xs text-gray-500">{formatDateTime(activity.createdAt)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <FormActions>
              <button type="button" onClick={() => setViewing(null)} className={secondaryButtonClass}>
                Close
              </button>
              {isAdmin && !viewing.deletedAt && (
                <button type="button" onClick={() => openEdit(viewing)} className={primaryButtonClass}>
                  Edit User
                </button>
              )}
            </FormActions>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!turningOffTwoFactor}
        onClose={() => setTurningOffTwoFactor(null)}
        onConfirm={confirmTwoFactorReset}
        title="Turn off two-step sign-in?"
        message={
          turningOffTwoFactor
            ? `${fullName(turningOffTwoFactor)} will sign in with the password alone until they set up two-step sign-in again on their profile. Do this only when you are sure it is them asking, for example when their phone was lost.`
            : ''
        }
        confirmLabel="Turn off"
        pendingLabel="Turning off…"
        isPending={resetTwoFactor.isPending}
      />

      {/* Reset password */}
      <Modal
        isOpen={!!resetting}
        onClose={closeReset}
        title="Reset password"
        description={resetting ? `Set a new password for ${fullName(resetting)} (${resetting.email}).` : undefined}
        size="md"
      >
        {resetting && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <Field label="New Password" htmlFor="reset-password" required>
              <PasswordInput id="reset-password" value={newPassword} onChange={setNewPassword} />
            </Field>
            <div>
              <button type="button" onClick={() => setNewPassword(generatePassword())} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                Generate a password
              </button>
              <PasswordChecklist value={newPassword} />
            </div>
            <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-600/20">
              The user is not told about the change. Pass the new password on yourself.
              {isLocked(resetting) && ' This also unlocks the account.'}
            </p>
            <FormActions>
              <button type="button" onClick={closeReset} disabled={resetPassword.isPending} className={secondaryButtonClass}>
                Cancel
              </button>
              <button type="submit" disabled={!isPasswordValid(newPassword) || resetPassword.isPending} className={primaryButtonClass}>
                {resetPassword.isPending ? 'Resetting…' : 'Reset Password'}
              </button>
            </FormActions>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete user"
        message={
          deleting ? (
            <>
              Delete <span className="font-semibold">{fullName(deleting)}</span> ({deleting.email})? They will no longer be able to sign in.
              The account is kept and can be restored from the Deleted filter.
            </>
          ) : null
        }
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={deleteUser.isPending}
      />
    </div>
  );
};

export default UserList;
